import { Controller } from "@hotwired/stimulus"

// Controller for CSV file upload with full-page drag & drop overlay
// Shows overlay when dragging files over page, hides when drag leaves or file drops
// Auto-submits form when file is selected
export default class extends Controller {
  static targets = ["form", "fileInput", "dropzone", "overlay", "backdrop"]

  // Animation delay (ms) - half of CSS transition duration for snappier feel
  static ANIMATION_DELAY_MS = 150

  connect() {
    console.log("[CSV Upload] Controller connected")

    // Initialize drag depth counter for nested element handling
    this.dragDepth = 0

    // Set up document-level drag & drop event listeners
    this.setupDocumentListeners()
  }

  disconnect() {
    console.log("[CSV Upload] Controller disconnecting")
    // Clean up all event listeners
    this.removeDocumentListeners()
  }

  // Setup document-level drag event listeners
  setupDocumentListeners() {
    this.boundDocumentDragEnter = this.handleDocumentDragEnter.bind(this)
    this.boundDocumentDragLeave = this.handleDocumentDragLeave.bind(this)
    this.boundDocumentDragOver = this.handleDocumentDragOver.bind(this)
    this.boundDocumentDrop = this.handleDocumentDrop.bind(this)
    this.boundDocumentDragEnd = this.handleDocumentDragEnd.bind(this)

    document.addEventListener('dragenter', this.boundDocumentDragEnter)
    document.addEventListener('dragleave', this.boundDocumentDragLeave)
    document.addEventListener('dragover', this.boundDocumentDragOver)
    document.addEventListener('drop', this.boundDocumentDrop)
    document.addEventListener('dragend', this.boundDocumentDragEnd)

    console.log("[CSV Upload] Document-level drag listeners registered")
  }

  // Remove document-level listeners
  removeDocumentListeners() {
    if (this.boundDocumentDragEnter) {
      document.removeEventListener('dragenter', this.boundDocumentDragEnter)
      document.removeEventListener('dragleave', this.boundDocumentDragLeave)
      document.removeEventListener('dragover', this.boundDocumentDragOver)
      document.removeEventListener('drop', this.boundDocumentDrop)
      document.removeEventListener('dragend', this.boundDocumentDragEnd)
    }
  }


  // Document-level drag enter - show overlay if dragging files
  handleDocumentDragEnter(event) {
    // Only show overlay if dragging files (not text, images, etc.)
    const hasFiles = event.dataTransfer && event.dataTransfer.types.includes('Files')
    if (!hasFiles) return

    event.preventDefault()

    this.dragDepth++
    console.log("[CSV Upload] Drag enter (depth:", this.dragDepth, ")")

    if (this.dragDepth === 1) {
      this.showOverlay()
    }
  }

  // Document-level drag leave - hide overlay when leaving page
  handleDocumentDragLeave(event) {
    this.dragDepth--
    console.log("[CSV Upload] Drag leave (depth:", this.dragDepth, ")")

    if (this.dragDepth === 0) {
      this.hideOverlay()
    }
  }

  // Document-level drag over - prevent default to allow drop
  handleDocumentDragOver(event) {
    const hasFiles = event.dataTransfer && event.dataTransfer.types.includes('Files')
    if (!hasFiles) return

    event.preventDefault()
  }

  // Document-level drop - process file and hide overlay
  handleDocumentDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    console.log("[CSV Upload] Document drop event")

    // Reset drag depth
    this.dragDepth = 0

    // Process dropped files
    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.processFile(files[0])
    }

    // Hide overlay
    this.hideOverlay()
  }

  // Document-level drag end - hide overlay when drag operation ends
  // This is CRITICAL for fixing the drag-out issue
  handleDocumentDragEnd(event) {
    console.log("[CSV Upload] Drag ended - force hide overlay")

    // Always force hide and reset on drag end
    // This handles cases where user drags out of window without dropping
    this.dragDepth = 0
    this.hideOverlay()
  }

  // Process a file (assign to input and submit form)
  processFile(file) {
    if (!file) return

    console.log("[CSV Upload] Processing file:", file.name)

    // Check file type if accept attribute is set
    const acceptAttr = this.fileInputTarget.getAttribute('accept')
    if (acceptAttr && !this.isFileTypeAccepted(file, acceptAttr)) {
      console.warn("[CSV Upload] File type not accepted:", file.type)
      alert("Bitte wählen Sie eine CSV-Datei aus.")
      return
    }

    // Use DataTransfer to assign file to input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.fileInputTarget.files = dataTransfer.files

    console.log("[CSV Upload] File assigned, triggering form submission")
    this.selectFile()
  }

  // Check if file type is accepted based on accept attribute
  isFileTypeAccepted(file, accept) {
    const acceptedTypes = accept.split(',').map(t => t.trim())

    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        // Extension check (e.g., ".csv")
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      } else if (type.includes('*')) {
        // Wildcard check (e.g., "text/*")
        const [category] = type.split('/')
        return file.type.startsWith(category + '/')
      } else {
        // Exact MIME type check
        return file.type === type
      }
    })
  }

  // Trigger form submission when file is selected
  selectFile() {
    if (this.fileInputTarget.files.length > 0) {
      console.log("[CSV Upload] Submitting form")
      this.formTarget.requestSubmit()
    }
  }

  // Open native file picker (triggered by button click)
  openFilePicker() {
    console.log("[CSV Upload] Opening file picker")
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  // Show overlay with animation
  showOverlay() {
    if (!this.hasOverlayTarget) return

    console.log("[CSV Upload] Showing overlay")

    // Show overlay container (pointer-events only, no opacity)
    this.overlayTarget.classList.remove("pointer-events-none")
    this.overlayTarget.classList.add("pointer-events-auto")
    this.overlayTarget.setAttribute("aria-hidden", "false")

    // Fade in backdrop (opacity transition)
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-0")
      this.backdropTarget.classList.add("opacity-100")
    }

    // Animate dropzone (scale up from 95% to 100%)
    if (this.hasDropzoneTarget) {
      // Start state
      this.dropzoneTarget.classList.add("scale-95", "opacity-0")

      // Trigger animation after a tick (for transition to work)
      requestAnimationFrame(() => {
        this.dropzoneTarget.classList.remove("scale-95", "opacity-0")
        this.dropzoneTarget.classList.add("scale-100", "opacity-100")
      })
    }
  }

  // Hide overlay with animation
  hideOverlay() {
    if (!this.hasOverlayTarget) return

    console.log("[CSV Upload] Hiding overlay")

    // Fade out backdrop (opacity transition)
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-100")
      this.backdropTarget.classList.add("opacity-0")
    }

    // Animate dropzone out
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("scale-100", "opacity-100")
      this.dropzoneTarget.classList.add("scale-95", "opacity-0")
    }

    // Hide overlay after brief delay for animation
    setTimeout(() => {
      this.overlayTarget.classList.remove("pointer-events-auto")
      this.overlayTarget.classList.add("pointer-events-none")
      this.overlayTarget.setAttribute("aria-hidden", "true")

      // Reset drag depth
      this.dragDepth = 0
    }, this.constructor.ANIMATION_DELAY_MS)
  }

  // Stop propagation - prevents clicks on dropzone from closing overlay
  stopPropagation(event) {
    event.stopPropagation()
  }
}
